<script>
    // ===============================
    // IMPORTS
    // ===============================

    // Svelte lifecycle hook
    import { onMount } from 'svelte'; // For initializing the table on mount

    // Utility function for comparing values
    import { compareValues, numberWithDelimiter } from './utils';
    // Importing icons and components
    import ArrowDownToLine from 'lucide-svelte/icons/arrow-down-to-line';
    import ArrowUpFromLine from 'lucide-svelte/icons/arrow-up-from-line';
    import FilterX from 'lucide-svelte/icons/filter-x';
    import TableHeader from './TableHeader.svelte';
    import TableRow from './TableRow.svelte';

    // ===============================
    // COMPONENT PROPS
    // ===============================
    export let headers = []; // List of table headers
    export let headerOptions = {}; // Options for each header (e.g., sortable, filterable)
    export let rows = []; // Data rows to display in the table
    export let collapsedRows = null; // Number of rows to display when collapsed
    export let sorted = {}; // Initial sort configuration (optional)
    export let footnote = null; // Footnote to display beneath the table (optional)

    // ===============================
    // LOCAL STATE
    // ===============================
    let isCollapsed = true; // Toggle for showing more/less rows
    let sortedColumn = ''; // The currently sorted column
    let sortDirection = 'desc'; // The direction of the current sort ('asc' or 'desc')
    let filters = {}; // To store the current filters for each column
    let uniqueValues = {}; // To store unique values for filterable columns

    // ===============================
    // REACTIVE STATEMENTS
    // ===============================
    $: {
        uniqueValues = headers.reduce((acc, header) => {
            if (headerOptions[header]?.filterable) {
                acc[header] = [...new Set(rows.map((row) => row[headers.indexOf(header)]))].sort();
            }
            return acc;
        }, {});
    }

    // The rows to display after applying filters
    $: filteredRows = [...rows];

    // Stores the min and max values for each column
    $: columnRanges = headers.reduce((acc, header, index) => {
        const values = rows.map((row) => row[index]);
        acc[header] = {
            min: Math.min(...values.filter((value) => !isNaN(value))),
            max: Math.max(...values.filter((value) => !isNaN(value))),
        };
        return acc;
    }, {});

    $: visibleRows = isCollapsed && collapsedRows ? filteredRows.slice(0, collapsedRows) : filteredRows;

    $: showMoreButton = collapsedRows != null && filteredRows.length > collapsedRows;

    $: tableIsFixed = headers.some((header) => headerOptions[header]?.searchable || headerOptions[header]?.filterable);

    $: tableIsFiltered = Object.keys(filters).some((header) => {
        const filter = filters[header];

        // On filterable columns, don't count the table as filtered if all options are selected
        if (filter?.type === 'selection') {
            const allValuesSelected = filter.selected && filter.selected.size === uniqueValues[header].length;
            return !allValuesSelected;
        }

        return true;
    });

    // ===============================
    // LIFECYCLE METHODS
    // ===============================
    onMount(() => {
        const initialSort = Object.keys(sorted)[0];
        if (initialSort) {
            sortedColumn = initialSort;
            sortDirection = sorted[initialSort];

            headerOptions[sortedColumn].sortable = true;

            sortTable(sortedColumn, sortDirection);
        }

        // Initialize filters to select all options in filterable columns
        headers.forEach((header, index) => {
            if (headerOptions[header]?.filterable) {
                const uniqueValuesForColumn = [...new Set(rows.map((row) => row[index]))];
                filters[header] = {
                    type: 'selection',
                    selected: new Set(uniqueValuesForColumn),
                };
            }
        });

        filters = { ...filters };
    });

    // ===============================
    // EVENT HANDLERS
    // ===============================

    /**
     * Toggle the collapse state of the table.
     */
    const toggleCollapse = () => {
        isCollapsed = !isCollapsed;
    };

    /**
     * Clear all filters and reset the table to its original state.
     */
    const clearAllFilters = () => {
        filters = {};
        filteredRows = [...rows];
        filterTable();
    };

    /**
     * Sort the table by the specified column.
     * @param {string} column - The column to sort by.
     * @param {string|null} direction - The direction to sort ('asc' or 'desc'). If null, toggle the direction.
     */
    const sortTable = (column, direction = null) => {
        if (direction) {
            sortDirection = direction;
        } else {
            if (sortedColumn === column) {
                sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                sortDirection = 'asc';
            }
        }

        sortedColumn = column;

        filteredRows = [...filteredRows].sort((a, b) => {
            const aValue = a[headers.indexOf(sortedColumn)];
            const bValue = b[headers.indexOf(sortedColumn)];
            return compareValues(aValue, bValue, sortDirection);
        });
    };

    /**
     * Filter the table rows based on the current filters.
     */
    const filterTable = () => {
        filteredRows = rows.filter((row) => {
            return headers.every((header) => {
                const filter = filters[header];
                if (!filter) return true;

                const cellValue = row[headers.indexOf(header)].toString().toLowerCase();

                // Apply search filter
                if (filter.type === 'search') {
                    if (!cellValue.includes(filter.query)) {
                        return false;
                    }
                }

                // Apply selected filters (checkboxes)
                if (filter.type === 'selection') {
                    const selectedValues = new Set(
                        Array.from(filter.selected).map((value) => value.trim().toLowerCase()),
                    );
                    if (selectedValues.size === 0) return false;
                    const normalizedCellValue = cellValue.trim().toLowerCase();
                    if (!selectedValues.has(normalizedCellValue)) {
                        return false;
                    }
                }

                // Apply range filters
                if (filter.type === 'range') {
                    const numericValue = Number(row[headers.indexOf(header)]);
                    if (filter.filterMode === 'Between') {
                        if (numericValue < filter.minValue || numericValue > filter.maxValue) {
                            return false;
                        }
                    } else if (filter.filterMode === 'Equals') {
                        if (numericValue !== filter.minValue) {
                            return false;
                        }
                    }
                }

                // If no filter blocked the row, it should be included
                return true;
            });
        });

        // Apply sorting
        if (sortedColumn) sortTable(sortedColumn, sortDirection);
    };

    /**
     * Handle sort events from the TableHeader component.
     * @param {Object} event - The sort event.
     */
    const handleSort = (event) => {
        const { header, direction } = event.detail;
        sortTable(header, direction);
    };

    /**
     * Handle search filter events from the TableHeader component.
     * @param {Object} event - The search event.
     */
    const handleSearch = (event) => {
        const { header, query } = event.detail;
        if (query === '') {
            delete filters[header];
        } else {
            filters[header] = { type: 'search', query };
        }
        filters = { ...filters };
        filterTable();
    };

    /**
     * Handle checkbox filter events from the TableHeader component.
     * @param {Object} event - The filter event.
     */
    const handleFilterChange = (event) => {
        const { header, selectedFilters: newSelectedFilters } = event.detail;
        filters[header] = { type: 'selection', selected: new Set(newSelectedFilters) };
        filters = { ...filters };
        filterTable();
    };

    /**
     * Handle range filter events from the TableHeader component.
     * @param {Object} event - The range filter event.
     */
    const handleRangeFilter = (event) => {
        const { header, filterMode, minValue, maxValue } = event.detail;
        if (filterMode === 'Between') {
            filters[header] = { type: 'range', filterMode, minValue, maxValue };
        } else if (filterMode === 'Equals') {
            filters[header] = { type: 'range', filterMode, minValue, maxValue };
        }
        filters = { ...filters };
        filterTable();
    };
</script>

<div>
    <div class="table-container">
        <table class:fixed={tableIsFixed}>
            <TableHeader
                {headers}
                {headerOptions}
                {sortedColumn}
                {sortDirection}
                {uniqueValues}
                {columnRanges}
                {filters}
                on:search={handleSearch}
                on:filter={handleFilterChange}
                on:rangeFilter={handleRangeFilter}
                on:sort={handleSort}
            />
            <tbody>
                {#each visibleRows as row}
                    <TableRow {row} {headers} {headerOptions} />
                {/each}
            </tbody>
        </table>

        {#if footnote}
            <p class="footnote">{footnote}</p>
        {/if}
    </div>

    {#if showMoreButton}
        <button on:click={toggleCollapse}>
            {#if isCollapsed}
                <ArrowDownToLine /> Show All ({numberWithDelimiter(filteredRows.length)})
            {:else}
                <ArrowUpFromLine /> Show Fewer
            {/if}
        </button>
    {/if}

    {#if tableIsFiltered}
        <button on:click={clearAllFilters}><FilterX /> Clear Filters</button>
    {/if}
</div>

<style lang="scss">
    button {
        border: 1px solid #006997;
        border-radius: 4px;
        background-color: #006997;
        color: #fff;
        font-size: var(--scale-1);
    }

    button:hover {
        border-color: #06aee0;
        background-color: #06aee0;
    }

    table {
        width: max-content;
        min-width: 100%;
        margin: var(--spacing0) auto;
        border-collapse: collapse;
    }

    table.fixed {
        width: 100%;
    }

    @media screen and (width >= 760px) {
        table.fixed {
            // table-layout: fixed; // Safari 18.0.1 (20619.1.26.31.7) does not like this!
        }
    }

    .table-container {
        overflow-x: auto;
    }

    .footnote {
        color: var(--color-text-supp);
        font-size: smaller;
    }
</style>
